/**
 * The onboarding option lists from `@shared/types`, turned into translated
 * chip options. Slug groups translate as `onboardingOptions.<group>.<slug>`;
 * designer services and styles store English labels, so they map through the
 * key tables below and fall back to the stored text.
 */

import {
	CONTRACTOR_PROJECT_TYPES,
	DESIGN_STYLES,
	DESIGNER_PROJECT_TYPES,
	DESIGNER_SERVICES,
	HOMEOWNER_GOALS,
	HOMEOWNER_PROPERTY_TYPES,
	PROPERTY_STATUSES,
	REALTOR_CLIENT_TYPES,
	REALTOR_PROPERTY_TYPES,
} from "@shared/types";
import type { TFunction } from "i18next";
import type { ChipOption } from "./components/fields/option-chips";

const DESIGNER_SERVICE_KEYS: Record<
	(typeof DESIGNER_SERVICES)[number],
	string
> = {
	"Space Planning": "spacePlanning",
	"Interior Design": "interiorDesign",
	"Interior Decoration": "interiorDecoration",
	"3D Visualization / Rendering": "visualization3d",
	"Material & Finish Selection": "materialFinishSelection",
	"Furniture Selection": "furnitureSelection",
	"Lighting Design": "lightingDesign",
	"Kitchen & Bath Design": "kitchenBathDesign",
	"Custom Furniture / Millwork": "customFurnitureMillwork",
	"Project Management": "projectManagement",
	"Styling / Home Staging": "stylingHomeStaging",
	Consultation: "consultation",
};

const DESIGN_STYLE_KEYS: Record<(typeof DESIGN_STYLES)[number], string> = {
	Contemporary: "contemporary",
	Modern: "modern",
	Traditional: "traditional",
	Minimalist: "minimalist",
	Industrial: "industrial",
	Scandinavian: "scandinavian",
	Bohemian: "bohemian",
	"Mid-Century Modern": "midCenturyModern",
	"Art Deco": "artDeco",
};

function slugOptions(
	t: TFunction,
	group: string,
	values: readonly string[],
): ChipOption[] {
	return values.map((value) => ({
		value,
		label: t(`onboardingOptions.${group}.${value}`),
	}));
}

export const goalOptions = (t: TFunction) =>
	slugOptions(t, "homeownerGoals", HOMEOWNER_GOALS);

export const homeownerPropertyTypeOptions = (t: TFunction) =>
	slugOptions(t, "homeownerPropertyTypes", HOMEOWNER_PROPERTY_TYPES);

export const propertyStatusOptions = (t: TFunction) =>
	slugOptions(t, "propertyStatuses", PROPERTY_STATUSES);

export const designerProjectTypeOptions = (t: TFunction) =>
	slugOptions(t, "designerProjectTypes", DESIGNER_PROJECT_TYPES);

export const contractorProjectTypeOptions = (t: TFunction) =>
	slugOptions(t, "contractorProjectTypes", CONTRACTOR_PROJECT_TYPES);

export const realtorPropertyTypeOptions = (t: TFunction) =>
	slugOptions(t, "realtorPropertyTypes", REALTOR_PROPERTY_TYPES);

export const realtorClientTypeOptions = (t: TFunction) =>
	slugOptions(t, "realtorClientTypes", REALTOR_CLIENT_TYPES);

/** The designer service list, plus any stored service no longer on it. */
export function designerServiceOptions(
	t: TFunction,
	stored: readonly string[] = [],
): ChipOption[] {
	const listed: ChipOption[] = DESIGNER_SERVICES.map((value) => ({
		value,
		label: t(
			`onboardingOptions.designerServices.${DESIGNER_SERVICE_KEYS[value]}`,
		),
	}));
	return withStored(listed, stored);
}

/** The style list, plus any stored or custom style not on it. */
export function designStyleOptions(
	t: TFunction,
	stored: readonly string[] = [],
): ChipOption[] {
	const listed: ChipOption[] = DESIGN_STYLES.map((value) => ({
		value,
		label: t(`options.designStyles.${DESIGN_STYLE_KEYS[value]}`),
	}));
	return withStored(listed, stored);
}

function withStored(
	listed: ChipOption[],
	stored: readonly string[],
): ChipOption[] {
	const extra = stored
		.filter((value) => !listed.some((option) => option.value === value))
		.map((value) => ({ value, label: value }));
	return [...listed, ...extra];
}

/** A homeowner property type in words, e.g. "Builder floor" or the Other text. */
export function propertyTypeLabel(
	t: TFunction,
	type: string,
	typeOther?: string | null,
): string {
	if (type === "other" && typeOther) return typeOther;
	return t(`onboardingOptions.homeownerPropertyTypes.${type}`, {
		defaultValue: type,
	});
}
